<template>
    <v-dialog
      :value="value"
      fullscreen
      persistent
      transition="dialog-bottom-transition"
      @input="$emit('input', $event)"
    >
      <v-card v-if="value" class="permissoes-wizard-card d-flex flex-column">
        <v-toolbar dark color="primary" flat dense extended class="permissoes-wizard-toolbar">
          <v-btn icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title class="text-truncate">{{ dialogTitle }}</v-toolbar-title>
          <v-spacer />
          <v-btn
            v-if="wizardStep === 2"
            dark
            text
            :loading="saving"
            :disabled="saving || !canSave"
            @click="save"
          >
            {{ $t('permissoes.form.buttons.save') }}
          </v-btn>
          <template v-slot:extension>
            <div class="permissoes-wizard-toolbar-extension d-flex align-center">
              <v-btn text dark small :disabled="wizardStep === 0 || isEditMode" @click="wizardBack">
                <v-icon left>mdi-arrow-left</v-icon>
                {{ $t('permissoes.wizard.back') }}
              </v-btn>
              <v-spacer />
              <div class="permissoes-wizard-toolbar-step d-flex flex-column align-center mx-2">
                <span class="caption white--text">
                  {{ $t('permissoes.wizard.progress', { current: wizardStep + 1, total: 3 }) }}
                </span>
                <span class="body-2 white--text font-weight-medium text-truncate permissoes-wizard-step-title">
                  {{ stepTitle }}
                </span>
              </div>
              <v-spacer />
              <v-btn
                text
                dark
                small
                :disabled="wizardStep >= 2 || !canGoNext"
                :loading="resolving"
                @click="wizardNext"
              >
                {{ $t('permissoes.wizard.next') }}
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </template>
        </v-toolbar>
  
        <v-card-text class="pa-4 flex-grow-1" style="overflow-y: auto">
          <!-- Step 0: type -->
          <v-container v-show="wizardStep === 0">
            <v-row v-if="!isEditMode">
              <v-col cols="12">
                <div class="subtitle-1 mb-2">{{ $t('permissoes.wizard.selectType') }}</div>
                <v-item-group v-model="permissionType" mandatory>
                  <v-row>
                    <v-col
                      v-for="opt in typeOptions"
                      :key="opt.value"
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <v-item v-slot="{ active, toggle }" :value="opt.value">
                        <v-card
                          :color="active ? 'primary lighten-5' : ''"
                          outlined
                          class="pa-3"
                          style="cursor: pointer; height: 100%"
                          @click="toggle"
                        >
                          <div class="font-weight-medium">{{ opt.label }}</div>
                          <div class="text-caption mt-1">{{ opt.hint }}</div>
                        </v-card>
                      </v-item>
                    </v-col>
                  </v-row>
                </v-item-group>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                <v-chip color="primary" outlined>
                  {{ typeLabel(permissionType) }}
                </v-chip>
              </v-col>
            </v-row>
          </v-container>
  
          <!-- Step 1: entity picker -->
          <v-container v-show="wizardStep === 1">
            <v-progress-linear v-if="loadingEntities" indeterminate class="mb-4" />
  
            <template v-if="permissionType === 'screen'">
              <v-autocomplete
                v-model="selectedScreen"
                :items="manifestScreens"
                :label="$t('permissoes.wizard.pickers.screen')"
                clearable
                :rules="[(v) => !!v || $t('permissoes.wizard.validation.screenRequired')]"
              />
            </template>
  
            <template v-else-if="permissionType === 'company'">
              <v-data-table
                v-model="selectedEmpresas"
                :headers="empresaHeaders"
                :items="empresas"
                item-key="id_empresa"
                show-select
                :loading="loadingEntities"
                :search="searchEmpresas"
                dense
                class="elevation-1"
              >
                <template v-slot:top>
                  <v-text-field
                    v-model="searchEmpresas"
                    :label="$t('permissoes.search')"
                    prepend-inner-icon="mdi-magnify"
                    dense
                    hide-details
                    class="ma-2"
                  />
                </template>
              </v-data-table>
            </template>
  
            <template v-else-if="permissionType === 'unit'">
              <v-data-table
                v-model="selectedUnidades"
                :headers="unidadeHeaders"
                :items="unidades"
                item-key="id_unidade"
                show-select
                :loading="loadingEntities"
                :search="searchUnidades"
                dense
                class="elevation-1"
              >
                <template v-slot:top>
                  <v-text-field
                    v-model="searchUnidades"
                    :label="$t('permissoes.search')"
                    prepend-inner-icon="mdi-magnify"
                    dense
                    hide-details
                    class="ma-2"
                  />
                </template>
              </v-data-table>
            </template>
  
            <template v-else-if="permissionType === 'dashboard'">
              <v-data-table
                v-model="selectedDashboards"
                :headers="dashboardHeaders"
                :items="dashboards"
                item-key="id_dashboard"
                show-select
                :loading="loadingEntities"
                :search="searchDashboards"
                dense
                class="elevation-1"
              >
                <template v-slot:top>
                  <v-text-field
                    v-model="searchDashboards"
                    :label="$t('permissoes.search')"
                    prepend-inner-icon="mdi-magnify"
                    dense
                    hide-details
                    class="ma-2"
                  />
                </template>
              </v-data-table>
            </template>
  
            <template v-else-if="permissionType === 'integration'">
              <v-data-table
                v-model="selectedEndpoints"
                :headers="endpointHeaders"
                :items="endpointRecursos"
                item-key="id_recurso"
                show-select
                :search="searchEndpoints"
                dense
                class="elevation-1"
              >
                <template v-slot:top>
                  <v-text-field
                    v-model="searchEndpoints"
                    :label="$t('permissoes.search')"
                    prepend-inner-icon="mdi-magnify"
                    dense
                    hide-details
                    class="ma-2"
                  />
                </template>
              </v-data-table>
            </template>
          </v-container>
  
          <!-- Step 2: review -->
          <v-container v-show="wizardStep === 2">
            <v-form ref="formStep2" v-model="formStep2Valid">
              <v-row class="mb-2" align="center">
                <v-col cols="12" md="8" lg="6">
                  <v-text-field
                    v-model="descricaoPermissao"
                    :label="$t('permissoes.form.fields.description')"
                    :rules="descriptionRules"
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
                <v-col
                  v-if="screenAudit && screenAudit.outdated"
                  cols="12"
                  md="4"
                  lg="6"
                >
                  <v-alert
                    type="warning"
                    :icon="false"
                    colored-border
                    border="left"
                    elevation="2"
                    dense
                    class="mb-0"
                  >
                    <div class="d-flex align-center flex-wrap" style="gap: 16px">
                      <span class="body-2">
                        {{ $t('permissoes.wizard.refresh.outdated', { count: screenAudit.missingReadCodes.length }) }}
                      </span>
                      <v-spacer />
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            fab
                            small
                            color="primary"
                            :loading="updatingFromManifest"
                            :disabled="updatingFromManifest"
                            @click="updateFromManifest"
                          >
                            <v-icon>mdi-sync</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('permissoes.wizard.refresh.button') }}</span>
                      </v-tooltip>
                    </div>
                  </v-alert>
                </v-col>
              </v-row>
            </v-form>
            <PermissaoResourceReview
              :resources="resolvedResources"
              :permission-type="permissionType"
              :missing-endpoints="missingEndpoints"
              :all-recursos="recursosCache"
              :allow-resource-edit="allowResourceEdit"
              @update:resources="resolvedResources = $event"
            />
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  import apiController from "@/utils/apiController";
  import EventBus from "@/utils/eventBus";
  import translateDbField from "@/utils/translateDbField";
  import formatPermissaoDescricao from "@/utils/formatPermissaoDescricao";
  import PermissaoResourceReview from "./PermissaoResourceReview.vue";
  import {
    PERMISSION_TYPES,
    dedupeResources,
    getManifestScreens,
    inferPermissionType,
    resolveDashboardResources,
    resolveEmpresaResources,
    resolveIntegrationResources,
    resolveScreenResources,
    resolveUnidadeResources,
  } from "@/utils/permissionResourceResolver";
  import { auditScreenPermission } from "@/utils/screenPermissionAudit";
  
  export default {
    name: "PermissaoWizardDialog",
    components: { PermissaoResourceReview },
    props: {
      value: { type: Boolean, default: false },
      token: { type: String, required: true },
      editedPermission: { type: Object, default: null },
    },
    data() {
      return {
        wizardStep: 0,
        formStep2Valid: false,
        permissionType: PERMISSION_TYPES.SCREEN,
        descricaoPermissao: "",
        translationKey: null,
        resolvedResources: [],
        missingEndpoints: [],
        recursosCache: [],
        empresas: [],
        unidades: [],
        dashboards: [],
        selectedScreen: null,
        selectedEmpresas: [],
        selectedUnidades: [],
        selectedDashboards: [],
        selectedEndpoints: [],
        searchEmpresas: "",
        searchUnidades: "",
        searchDashboards: "",
        searchEndpoints: "",
        loadingEntities: false,
        resolving: false,
        saving: false,
        updatingFromManifest: false,
        manifestScreens: getManifestScreens(),
      };
    },
    computed: {
      isEditMode() {
        return !!(this.editedPermission && this.editedPermission.id_permissao);
      },
      dialogTitle() {
        return this.isEditMode
          ? this.$t("permissoes.form.title.edit")
          : this.$t("permissoes.form.title.create");
      },
      stepTitle() {
        const keys = [
          "permissoes.wizard.steps.type",
          "permissoes.wizard.steps.entities",
          "permissoes.wizard.steps.review",
        ];
        return this.$t(keys[this.wizardStep]);
      },
      typeOptions() {
        return [
          {
            value: PERMISSION_TYPES.SCREEN,
            label: this.$t("permissoes.wizard.types.screen"),
            hint: this.$t("permissoes.wizard.typeHints.screen"),
          },
          {
            value: PERMISSION_TYPES.COMPANY,
            label: this.$t("permissoes.wizard.types.company"),
            hint: this.$t("permissoes.wizard.typeHints.company"),
          },
          {
            value: PERMISSION_TYPES.UNIT,
            label: this.$t("permissoes.wizard.types.unit"),
            hint: this.$t("permissoes.wizard.typeHints.unit"),
          },
          {
            value: PERMISSION_TYPES.DASHBOARD,
            label: this.$t("permissoes.wizard.types.dashboard"),
            hint: this.$t("permissoes.wizard.typeHints.dashboard"),
          },
          {
            value: PERMISSION_TYPES.INTEGRATION,
            label: this.$t("permissoes.wizard.types.integration"),
            hint: this.$t("permissoes.wizard.typeHints.integration"),
          },
        ];
      },
      descriptionRules() {
        return [(v) => !!v || this.$t("permissoes.form.validation.descriptionRequired")];
      },
      endpointRecursos() {
        return this.recursosCache.filter((r) => r.descricao_tipo_recurso === "Endpoint");
      },
      empresaHeaders() {
        return [
          { text: this.$t("permissoes.wizard.headers.company"), value: "razao_social" },
          { text: "ID", value: "id_empresa" },
        ];
      },
      unidadeHeaders() {
        return [
          { text: this.$t("permissoes.wizard.headers.unitCode"), value: "cod_unidade" },
          { text: this.$t("permissoes.wizard.headers.unitName"), value: "descricao_unidade" },
        ];
      },
      dashboardHeaders() {
        return [
          { text: this.$t("permissoes.wizard.headers.dashboardCode"), value: "cod_dashboard" },
          { text: this.$t("permissoes.wizard.headers.dashboardTitle"), value: "titulo_dashboard" },
        ];
      },
      endpointHeaders() {
        return [
          { text: this.$t("permissoes.wizard.headers.endpointCode"), value: "cod_recurso" },
          { text: this.$t("permissoes.wizard.headers.endpointRoute"), value: "rota" },
        ];
      },
      canGoNext() {
        if (this.wizardStep === 0) {
          return !!this.permissionType;
        }
        if (this.wizardStep === 1) {
          if (this.permissionType === PERMISSION_TYPES.SCREEN) return !!this.selectedScreen;
          if (this.permissionType === PERMISSION_TYPES.COMPANY) return this.selectedEmpresas.length > 0;
          if (this.permissionType === PERMISSION_TYPES.UNIT) return this.selectedUnidades.length > 0;
          if (this.permissionType === PERMISSION_TYPES.DASHBOARD) return this.selectedDashboards.length > 0;
          if (this.permissionType === PERMISSION_TYPES.INTEGRATION) return this.selectedEndpoints.length > 0;
        }
        return false;
      },
      canSave() {
        return this.resolvedResources.length > 0 && !!this.descricaoPermissao;
      },
      allowResourceEdit() {
        return [PERMISSION_TYPES.SCREEN, PERMISSION_TYPES.INTEGRATION].includes(
          this.permissionType
        ) || this.isEditMode;
      },
      screenAudit() {
        if (!this.isEditMode || this.permissionType !== PERMISSION_TYPES.SCREEN) {
          return null;
        }
        if (!this.resolvedResources.length) return null;
        const audit = auditScreenPermission(this.resolvedResources);
        return audit.isScreen && audit.knownScreen ? audit : null;
      },
    },
    watch: {
      value(open) {
        if (open) this.onOpen();
      },
      selectedUnidades() {
        this.updateAutoDescription();
      },
      selectedEmpresas() {
        this.updateAutoDescription();
      },
      selectedDashboards() {
        this.updateAutoDescription();
      },
    },
    methods: {
      typeLabel(type) {
        const opt = this.typeOptions.find((o) => o.value === type);
        return opt ? opt.label : type;
      },
      updateAutoDescription() {
        if (this.isEditMode || this.wizardStep !== 1) return;
  
        if (this.permissionType === PERMISSION_TYPES.UNIT) {
          const units = this.selectedUnidades;
          if (!units.length) return;
          const base = translateDbField("database.permissions.unitAccess", "Acesso Unidade");
          const codes = units.map((u) => u.cod_unidade).join(", ");
          this.descricaoPermissao = `${base} ${codes}`;
          return;
        }
  
        if (this.permissionType === PERMISSION_TYPES.COMPANY) {
          const empresas = this.selectedEmpresas;
          if (!empresas.length) return;
          const base = translateDbField("database.permissions.companyAccess", "Acesso Empresa");
          const labels = empresas.map((e) => e.razao_social || e.id_empresa).join(", ");
          this.descricaoPermissao = `${base} ${labels}`;
          return;
        }
  
        if (this.permissionType === PERMISSION_TYPES.DASHBOARD) {
          const dashboards = this.selectedDashboards;
          if (!dashboards.length) return;
          const base = translateDbField("database.permissions.dashboardAccess", "Acesso Dashboard");
          const codes = dashboards.map((d) => d.cod_dashboard).join(", ");
          this.descricaoPermissao = `${base} ${codes}`;
        }
      },
      async onOpen() {
        this.resetWizard();
        await this.loadRecursos();
        if (this.isEditMode) {
          await this.loadEditState();
        }
      },
      resetWizard() {
        this.wizardStep = this.isEditMode ? 2 : 0;
        this.permissionType = PERMISSION_TYPES.SCREEN;
        this.descricaoPermissao = "";
        this.translationKey = null;
        this.resolvedResources = [];
        this.missingEndpoints = [];
        this.selectedScreen = null;
        this.selectedEmpresas = [];
        this.selectedUnidades = [];
        this.selectedDashboards = [];
        this.selectedEndpoints = [];
      },
      async loadRecursos() {
        try {
          const response = await apiController.request(apiController.endpoints.getallRecursos, { silent: true });
          this.recursosCache = response.data;
        } catch (e) {
          EventBus.$emit("show-error", { error: e });
        }
      },
      async loadEditState() {
        const item = this.editedPermission;
        this.descricaoPermissao = formatPermissaoDescricao(item);
        this.translationKey = item.translation_key || null;
  
        try {
          const response = await apiController.request(apiController.endpoints.getRecursospermissao, { params: { id: item.id_permissao }, silent: true });
          this.resolvedResources = response.data;
          this.permissionType = inferPermissionType(this.resolvedResources) || PERMISSION_TYPES.INTEGRATION;
          this.wizardStep = 2;
        } catch (e) {
          EventBus.$emit("show-error", { error: e });
        }
      },
      async loadEntitiesIfNeeded() {
        if (this.loadingEntities) return;
        this.loadingEntities = true;
        try {
          const requests = [];
          if (this.permissionType === PERMISSION_TYPES.COMPANY && !this.empresas.length) {
            requests.push(
              (async () => {
                const r = await apiController.request(apiController.endpoints.getallEmpresas, { silent: true });
                this.empresas = r.data;
              })()
            );
          }
          if (this.permissionType === PERMISSION_TYPES.UNIT && !this.unidades.length) {
            requests.push(
              (async () => {
                const r = await apiController.request(apiController.endpoints.getallUnidades, { silent: true });
                this.unidades = r.data;
              })()
            );
          }
          if (this.permissionType === PERMISSION_TYPES.DASHBOARD && !this.dashboards.length) {
            requests.push(
              (async () => {
                const r = await apiController.request(apiController.endpoints.getallDashboards, { silent: true });
                this.dashboards = r.data;
              })()
            );
          }
          await Promise.all(requests);
        } catch (e) {
          EventBus.$emit("show-error", { error: e });
        } finally {
          this.loadingEntities = false;
        }
      },
      wizardBack() {
        if (this.wizardStep > 0) this.wizardStep -= 1;
      },
      async wizardNext() {
        if (this.wizardStep === 0) {
          this.wizardStep = 1;
          await this.loadEntitiesIfNeeded();
          return;
        }
        if (this.wizardStep === 1) {
          if (!this.canGoNext) return;
          await this.resolveResources();
          if (this.resolvedResources.length === 0) {
            EventBus.$emit("show-alert", {
              mensagem: this.$t("permissoes.wizard.warnings.noResources"),
              tipo: "warning",
            });
            return;
          }
          if (
            !this.isEditMode &&
            [PERMISSION_TYPES.UNIT, PERMISSION_TYPES.COMPANY, PERMISSION_TYPES.DASHBOARD].includes(
              this.permissionType
            )
          ) {
            this.updateAutoDescription();
          }
          this.wizardStep = 2;
        }
      },
      async resolveResources() {
        this.resolving = true;
        this.missingEndpoints = [];
        try {
          let result = { resolved: [], missing: [] };
          const cache = [...this.recursosCache];
  
          if (this.permissionType === PERMISSION_TYPES.SCREEN) {
            result = await resolveScreenResources(this.token, cache, this.selectedScreen);
          } else if (this.permissionType === PERMISSION_TYPES.COMPANY) {
            result = await resolveEmpresaResources(this.token, cache, this.selectedEmpresas);
          } else if (this.permissionType === PERMISSION_TYPES.UNIT) {
            result = await resolveUnidadeResources(this.token, cache, this.selectedUnidades);
          } else if (this.permissionType === PERMISSION_TYPES.DASHBOARD) {
            result = await resolveDashboardResources(this.token, cache, this.selectedDashboards);
          } else if (this.permissionType === PERMISSION_TYPES.INTEGRATION) {
            const ids = this.selectedEndpoints.map((e) => e.id_recurso);
            result = resolveIntegrationResources(cache, ids);
          }
  
          this.recursosCache = cache;
          this.resolvedResources = dedupeResources(result.resolved);
          this.missingEndpoints = result.missing || [];
        } catch (e) {
          EventBus.$emit("show-error", { error: e });
        } finally {
          this.resolving = false;
        }
      },
      async updateFromManifest() {
        const audit = this.screenAudit;
        if (!audit || !audit.outdated) return;

        const endpointByCod = new Map();
        for (const recurso of this.recursosCache) {
          if (recurso.descricao_tipo_recurso === "Endpoint") {
            endpointByCod.set(recurso.cod_recurso, recurso);
          }
        }

        const toAdd = [];
        let unresolved = 0;
        for (const cod of audit.missingReadCodes) {
          const recurso = endpointByCod.get(cod);
          if (recurso) {
            toAdd.push(recurso);
          } else {
            unresolved += 1;
          }
        }

        const unresolvedSuffix =
          unresolved > 0
            ? ` ${this.$t("permissoes.wizard.refresh.unresolved", { count: unresolved })}`
            : "";

        if (toAdd.length === 0) {
          EventBus.$emit("show-alert", {
            mensagem: this.$t("permissoes.wizard.refresh.done", { count: 0 }) + unresolvedSuffix,
            tipo: unresolved > 0 ? "warning" : "success",
          });
          return;
        }

        this.updatingFromManifest = true;
        try {
          const id = this.editedPermission.id_permissao;
          const pairs = toAdd.map((recurso) => [id, recurso.id_recurso]);
          await apiController.request(apiController.endpoints.insertRecursospermissaobulk, {
            data: pairs,
            silent: true,
          });
          this.resolvedResources = dedupeResources([...this.resolvedResources, ...toAdd]);
          this.$emit("updated", { id_permissao: id });
          EventBus.$emit("show-alert", {
            mensagem:
              this.$t("permissoes.wizard.refresh.done", { count: toAdd.length }) + unresolvedSuffix,
            tipo: unresolved > 0 ? "warning" : "success",
          });
        } catch (e) {
          EventBus.$emit("show-error", { error: e });
        } finally {
          this.updatingFromManifest = false;
        }
      },
      close() {
        this.$emit("input", false);
        this.$emit("closed");
        this.$nextTick(() => this.resetWizard());
      },
      async save() {
        if (this.$refs.formStep2 && !this.$refs.formStep2.validate()) return;
        if (!this.canSave) return;
        this.saving = true;
        try {
          const payload = {
            id_permissao: this.editedPermission?.id_permissao,
            descricao_permissao: this.descricaoPermissao,
          };
  
          let insertId = payload.id_permissao;
  
          if (this.isEditMode) {
            await apiController.request(apiController.endpoints.updatePermissao, { data: payload, silent: true });
            await apiController.request(apiController.endpoints.deleteRecursospermissaobulk, { data: { id_permissao: insertId }, silent: true });
          } else if (this.permissionType === PERMISSION_TYPES.UNIT) {
            const response = await apiController.request(apiController.endpoints.insertUnidadepermissao, {
              data: {
                descricao_permissao: this.descricaoPermissao,
                translation_key: "database.permissions.unitAccess",
              },
              silent: true,
            });
            insertId = response.data.insertId;
          } else {
            const response = await apiController.request(apiController.endpoints.insertPermissao, { data: { descricao_permissao: this.descricaoPermissao }, silent: true });
            insertId = response.data.insertId;
          }

          if (this.resolvedResources.length > 0) {
            const pairs = this.resolvedResources.map((r) => [insertId, r.id_recurso]);
            await apiController.request(apiController.endpoints.insertRecursospermissaobulk, { data: pairs, silent: true });
          }
  
          this.$emit("saved", {
            mensagem: this.$t("permissoes.wizard.messages.saveSuccess"),
            erro: false,
          });
          this.close();
        } catch (e) {
          EventBus.$emit("show-error", { error: e });
        } finally {
          this.saving = false;
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .permissoes-wizard-card {
    min-height: 100vh;
  }
  
  .permissoes-wizard-toolbar {
    flex-shrink: 0;
  }
  
  .permissoes-wizard-toolbar.v-toolbar--extended {
    height: 112px !important;
    min-height: 112px !important;
    max-height: 112px !important;
  }
  
  .permissoes-wizard-toolbar >>> .v-toolbar__content {
    height: 56px !important;
    min-height: 56px !important;
    max-height: 56px !important;
  }
  
  .permissoes-wizard-toolbar >>> .v-toolbar__extension {
    height: 56px !important;
    min-height: 56px !important;
    max-height: 56px !important;
    padding: 0 8px !important;
  }
  
  .permissoes-wizard-toolbar-extension {
    width: 100%;
    height: 56px;
    min-height: 56px;
    max-height: 56px;
    overflow: hidden;
  }
  
  .permissoes-wizard-toolbar-step {
    min-width: 0;
    max-width: 50%;
  }
  
  .permissoes-wizard-step-title {
    max-width: 100%;
    line-height: 1.2;
  }
  </style>